import type { InfiniteData } from '@tanstack/react-query';
import type { CampaignOverviewResponse } from 'clients/CampaignsClient/CampaignsClient.types';
import type { PaginatedResponse } from 'clients/types';

import { useMutation } from '@tanstack/react-query';
import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { Conversation } from 'components/ui/organisms/CentralInbox/CentralInbox.types';
import { queryClient } from 'config/queryClient';
import { useContext } from 'react';
import { UserContext } from 'shared/legacy/contexts/UserContext/UserContext';
import { QueryKey } from 'types/query';
import { useCurrentUserQuery } from './useCurrentUserQuery';

export const useMarkBatchAsReadMutation = () => {
  const { data: currentUser } = useCurrentUserQuery();
  const { isImpersonated } = useContext(UserContext);
  return useMutation(
    async () => {
      await campaignsClient.markBatchAsRead();
    },
    {
      onSuccess: () => {
        if (isImpersonated) return;
        queryClient.invalidateQueries([QueryKey.useCandidateActivityLogQuery], { exact: false });

        const campaignQuery = queryClient
          .getQueryCache()
          .find([QueryKey.campaignManagerContextUseCampaigns], { exact: false, stale: false, type: 'active' });

        if (campaignQuery) {
          queryClient.setQueryData<InfiniteData<PaginatedResponse<CampaignOverviewResponse>>>(
            campaignQuery.queryKey,
            (data) => {
              if (!data) return data;

              return {
                pageParams: data.pageParams,
                pages: data.pages.map((page) => {
                  return {
                    ...page,
                    results: page.results.map((campaignResult) => {
                      return { ...campaignResult, new_replies_count: 0 };
                    }),
                  };
                }),
              };
            },
          );
        }

        queryClient.setQueriesData<InfiniteData<PaginatedResponse<Conversation>>>(
          {
            queryKey: [QueryKey.getInfiniteConversations],
            exact: false,
            stale: false,
          },
          (data) => {
            if (!data) return data;
            return {
              pageParams: data.pageParams,
              pages: data.pages.map((page: PaginatedResponse<Conversation>) => {
                return {
                  ...page,
                  results: page.results.map((conversation: Conversation) => {
                    if (!conversation.candidate) return conversation;
                    if (conversation.user.id !== currentUser?.id) return conversation;
                    return { ...conversation, unreadCount: 0 };
                  }),
                };
              }),
            };
          },
        );

        queryClient.setQueryData([QueryKey.useGetCampaignNewRepliesQuery], (data: number | undefined) => {
          if (!data) return;

          return 0;
        });

        setTimeout(() => {
          queryClient.invalidateQueries([QueryKey.getInfiniteConversations], { exact: false });
          queryClient.invalidateQueries([QueryKey.useGetUnreadConversationsAmount], { exact: false });
          queryClient.invalidateQueries([QueryKey.campaignManagerContextUseCampaigns], { exact: false });
          queryClient.invalidateQueries([QueryKey.useCandidateActivityLogQuery], { exact: false });
        }, 3000);
      },
    },
  );
};
